import type { OverviewDto, RadarPointDto } from '../types';
import { COMPANY_CARDS, DOSSIERS, subSegmentsOf } from './companies';

// Radar + Overview fixtures, derived from the company fixtures so the tabs stay
// mutually consistent (WWD principle: aggregates are computed, never hand-typed twice).

/** 30-day score deltas per slug — null = newly scored (no history to diff). */
const DELTAS: Record<string, { fit: number | null; availability: number | null }> = {
  'gulf-coast-drilling-systems': { fit: 0, availability: 21 },
  'rheinland-antriebstechnik': { fit: -2, availability: 14 },
  'baltic-mud-systems': { fit: 0, availability: 11 },
  'aberdeen-rig-components': { fit: 3, availability: 9 },
  'lone-star-rig-electrics': { fit: 0, availability: 8 },
  'meridian-well-automation': { fit: 4, availability: 0 },
  'nordic-drilltech': { fit: 0, availability: 0 },
  'fjordane-hydraulikk': { fit: 0, availability: 4 },
  // caledonia / hansa / straits / vestland / pennine: first score this cycle → null deltas
};

export const RADAR: RadarPointDto[] = COMPANY_CARDS.filter(
  (c) => c.fitScore !== null && c.availabilityScore !== null
).map((c) => ({
  slug: c.slug,
  name: c.name,
  subSegments: subSegmentsOf(c.slug),
  sizeBand: c.sizeBand,
  distressStage: c.distressStage,
  watchlistStage: c.watchlistStage,
  fit: c.fitScore as number,
  availability: c.availabilityScore as number,
  fitDelta30d: DELTAS[c.slug]?.fit ?? null,
  availabilityDelta30d: DELTAS[c.slug]?.availability ?? null,
}));

function absDelta(p: RadarPointDto): number {
  return Math.abs(p.fitDelta30d ?? 0) + Math.abs(p.availabilityDelta30d ?? 0);
}

const RECENT_EVENT_PICKS: Array<{ slug: string; eventId: number }> = [
  { slug: 'straits-rig-power', eventId: 161 },
  { slug: 'aberdeen-rig-components', eventId: 111 },
  { slug: 'aberdeen-rig-components', eventId: 113 },
  { slug: 'gulf-coast-drilling-systems', eventId: 121 },
  { slug: 'lone-star-rig-electrics', eventId: 171 },
  { slug: 'nordic-drilltech', eventId: 101 },
];

export const OVERVIEW: OverviewDto = {
  universeSize: 187,
  scoredCompanies: RADAR.length,
  activeDistressProcesses: 1,
  events7d: 4,
  watchlistCounts: {
    radar: COMPANY_CARDS.filter((c) => c.watchlistStage === 'radar').length,
    watch: COMPANY_CARDS.filter((c) => c.watchlistStage === 'watch').length,
    qualified: COMPANY_CARDS.filter((c) => c.watchlistStage === 'qualified').length,
    engaged: COMPANY_CARDS.filter((c) => c.watchlistStage === 'engaged').length,
    passed: COMPANY_CARDS.filter((c) => c.watchlistStage === 'passed').length,
  },
  topMovers: [...RADAR].filter((p) => absDelta(p) > 0).sort((a, b) => absDelta(b) - absDelta(a)).slice(0, 5),
  recentEvents: RECENT_EVENT_PICKS.flatMap(({ slug, eventId }) => {
    const dossier = DOSSIERS[slug];
    const ev = dossier?.events.find((e) => e.id === eventId);
    return ev ? [{ ...ev, companySlug: slug, companyName: dossier.name }] : [];
  }).sort((a, b) => b.eventDate.localeCompare(a.eventDate)),
};
